import { useEffect, useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	MultiSelect,
	type MultiSelectOption,
} from "@/components/ui/multi-select";
import { useCategoriesList, useProductsList } from "@/hooks/use-catalog";
import type {
	AutomaticDiscount,
	AutomaticDiscountType,
	CustomerScope,
} from "@/lib/promotions/automatic-discounts-api";
import {
	useCreateAutomaticDiscount,
	useUpdateAutomaticDiscount,
} from "@/lib/promotions/automatic-discounts-hooks";

interface AutomaticDiscountModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData?: AutomaticDiscount | null;
	onSuccess?: () => void;
}

function InfoTooltip({
	text,
	side = "bottom",
	align = "center",
}: {
	text: string;
	side?: "top" | "bottom";
	align?: "center" | "left" | "right";
}) {
	const alignClasses =
		align === "right"
			? "right-0 translate-x-0"
			: align === "left"
				? "left-0 translate-x-0"
				: "left-1/2 -translate-x-1/2";

	const arrowAlignClasses =
		align === "right"
			? "right-3 translate-x-0"
			: align === "left"
				? "left-3 translate-x-0"
				: "left-1/2 -translate-x-1/2";

	return (
		<span className="group relative inline-flex items-center ml-1.5 cursor-help text-slate-400 hover:text-[var(--a-terracotta)] transition-colors">
			<AdminIcon n="info" s={13} c="#64748b" />
			<span
				className={`pointer-events-none absolute hidden w-64 rounded-md bg-slate-900 p-2.5 text-[11px] font-normal text-slate-100 shadow-2xl group-hover:block z-100 leading-relaxed text-center ${alignClasses} ${
					side === "top" ? "bottom-full mb-2" : "top-full mt-2"
				}`}
			>
				{text}
				<span
					className={`absolute border-4 border-transparent ${arrowAlignClasses} ${
						side === "top"
							? "top-full border-t-slate-900 border-b-transparent"
							: "bottom-full border-b-slate-900 border-t-transparent"
					}`}
				/>
			</span>
		</span>
	);
}

export function AutomaticDiscountModal({
	isOpen,
	onClose,
	initialData,
	onSuccess,
}: AutomaticDiscountModalProps) {
	const isEditing = Boolean(initialData);

	const [activeTab, setActiveTab] = useState<
		"general" | "eligibility" | "targeting"
	>("general");

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [type, setType] = useState<AutomaticDiscountType>("PERCENT");
	const [value, setValue] = useState<number | "">(10);
	const [priority, setPriority] = useState<number | "">(10);
	const [minSubtotal, setMinSubtotal] = useState<number | "">(0);
	const [maxDiscount, setMaxDiscount] = useState<number | "">(0);
	const [isActive, setIsActive] = useState(true);
	const [startsAt, setStartsAt] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [customerScope, setCustomerScope] = useState<CustomerScope>("ALL");
	const [allowCumulationWithCoupons, setAllowCumulationWithCoupons] =
		useState(true);
	const [allowCumulationWithOtherAuto, setAllowCumulationWithOtherAuto] =
		useState(false);
	const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
	const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { data: categories = [] } = useCategoriesList({ isActive: true });
	const { data: productsData } = useProductsList({ pageSize: 100 });

	const createMutation = useCreateAutomaticDiscount();
	const updateMutation = useUpdateAutomaticDiscount();

	const isPending = createMutation.isPending || updateMutation.isPending;

	const categoryOptions: MultiSelectOption[] = useMemo(() => {
		return categories.map((cat) => ({
			value: cat.id,
			label: cat.name,
			badge: cat.slug,
		}));
	}, [categories]);

	const productOptions: MultiSelectOption[] = useMemo(() => {
		const items = productsData?.items || [];
		if (selectedCategoryIds.length === 0) {
			return items.map((p) => ({
				value: p.id,
				label: p.title,
				badge: p.sku,
			}));
		}

		const targetCategoryIds = new Set<string>();
		for (const catId of selectedCategoryIds) {
			targetCategoryIds.add(catId);
			const catObj = categories.find((c) => c.id === catId);
			if (catObj?.children) {
				for (const child of catObj.children) {
					targetCategoryIds.add(child.id);
				}
			}
			for (const c of categories) {
				if (c.parentId === catId) {
					targetCategoryIds.add(c.id);
				}
			}
		}

		const filteredItems = items.filter(
			(p) =>
				targetCategoryIds.has(p.categoryId) ||
				selectedProductIds.includes(p.id),
		);

		return filteredItems.map((p) => ({
			value: p.id,
			label: p.title,
			badge: p.sku,
		}));
	}, [productsData, selectedCategoryIds, selectedProductIds, categories]);

	useEffect(() => {
		if (!isOpen) return;

		if (initialData) {
			setName(initialData.name);
			setDescription(initialData.description || "");
			setType(initialData.type);
			setValue(initialData.value);
			setPriority(initialData.priority ?? 10);
			setMinSubtotal(initialData.minSubtotal || 0);
			setMaxDiscount(initialData.maxDiscount || 0);
			setIsActive(initialData.isActive);
			setStartsAt(
				initialData.startsAt
					? new Date(initialData.startsAt).toISOString().slice(0, 16)
					: "",
			);
			setExpiresAt(
				initialData.expiresAt
					? new Date(initialData.expiresAt).toISOString().slice(0, 16)
					: "",
			);
			setCustomerScope(initialData.customerScope || "ALL");
			setAllowCumulationWithCoupons(
				initialData.allowCumulationWithCoupons !== undefined
					? Boolean(initialData.allowCumulationWithCoupons)
					: true,
			);
			setAllowCumulationWithOtherAuto(
				initialData.allowCumulationWithOtherAuto !== undefined
					? Boolean(initialData.allowCumulationWithOtherAuto)
					: false,
			);
			setSelectedCategoryIds(initialData.categoryIds || []);
			setSelectedProductIds(initialData.productIds || []);
		} else {
			setName("");
			setDescription("");
			setType("PERCENT");
			setValue(10);
			setPriority(10);
			setMinSubtotal(0);
			setMaxDiscount(0);
			setIsActive(true);
			setStartsAt("");
			setExpiresAt("");
			setCustomerScope("ALL");
			setAllowCumulationWithCoupons(true);
			setAllowCumulationWithOtherAuto(false);
			setSelectedCategoryIds([]);
			setSelectedProductIds([]);
		}
		setErrorMessage(null);
		setActiveTab("general");
	}, [initialData, isOpen]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage(null);

		if (!name.trim()) {
			setErrorMessage("Le nom de la remise automatique est obligatoire.");
			setActiveTab("general");
			return;
		}

		if (value === "" || Number(value) < 0) {
			setErrorMessage(
				"La valeur de la remise doit être supérieure ou égale à 0.",
			);
			setActiveTab("general");
			return;
		}

		if (type === "PERCENT" && Number(value) > 100) {
			setErrorMessage(
				"Pour un pourcentage, la valeur ne peut pas dépasser 100%.",
			);
			setActiveTab("general");
			return;
		}

		if (startsAt && expiresAt && new Date(startsAt) >= new Date(expiresAt)) {
			setErrorMessage(
				"La date de début doit être strictement antérieure à la date de fin.",
			);
			setActiveTab("general");
			return;
		}

		const payload = {
			name: name.trim(),
			description: description.trim() || null,
			type,
			value: Number(value),
			priority: priority !== "" ? Number(priority) : 10,
			minSubtotal: minSubtotal !== "" ? Number(minSubtotal) : 0,
			maxDiscount: maxDiscount !== "" ? Number(maxDiscount) : 0,
			isActive,
			startsAt: startsAt ? new Date(startsAt).toISOString() : null,
			expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
			customerScope,
			allowCumulationWithCoupons,
			allowCumulationWithOtherAuto,
			categoryIds: selectedCategoryIds,
			productIds: selectedProductIds,
		};

		try {
			if (isEditing && initialData) {
				await updateMutation.mutateAsync({ id: initialData.id, data: payload });
			} else {
				await createMutation.mutateAsync(payload);
			}
			onSuccess?.();
			onClose();
		} catch (err: unknown) {
			const msg =
				err && typeof err === "object" && "message" in err
					? String((err as { message: string }).message)
					: "Une erreur est survenue lors de l'enregistrement de la remise automatique.";
			setErrorMessage(msg);
		}
	};

	const labelStyle = {
		display: "block",
		fontSize: 12,
		fontWeight: 600,
		color: "#475569",
		marginBottom: 5,
	};

	const inlineLabelStyle = {
		display: "inline-flex",
		alignItems: "center",
		fontSize: 12,
		fontWeight: 600,
		color: "#475569",
		marginBottom: 5,
	};

	if (!isOpen) return null;

	return (
		<Modal
			onClose={onClose}
			title={
				isEditing
					? `Modifier la remise : ${initialData?.name}`
					: "Créer une remise automatique (sans code)"
			}
			width={620}
		>
			<form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
				{/* Navigation par Onglets — Douce et compacte */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr 1fr",
						gap: 3,
						padding: 3,
						background: "var(--a-surface-2)",
						border: "1px solid var(--a-border)",
						borderRadius: 9,
						margin: "12px 16px 0",
					}}
				>
					<button
						type="button"
						onClick={() => setActiveTab("general")}
						className="active:scale-[0.96] whitespace-nowrap"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "6px 10px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "general" ? 600 : 500,
							cursor: "pointer",
							border:
								activeTab === "general"
									? "1px solid var(--a-border)"
									: "1px solid transparent",
							transition: "all 0.15s ease",
							background:
								activeTab === "general" ? "var(--a-surface)" : "transparent",
							color:
								activeTab === "general" ? "var(--a-ink)" : "var(--a-ink-mute)",
							boxShadow:
								activeTab === "general" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						1. Général & Type
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("eligibility")}
						className="active:scale-[0.96] whitespace-nowrap"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 4,
							padding: "6px 10px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "eligibility" ? 600 : 500,
							cursor: "pointer",
							border:
								activeTab === "eligibility"
									? "1px solid var(--a-border)"
									: "1px solid transparent",
							transition: "all 0.15s ease",
							background:
								activeTab === "eligibility"
									? "var(--a-surface)"
									: "transparent",
							color:
								activeTab === "eligibility"
									? "var(--a-ink)"
									: "var(--a-ink-mute)",
							boxShadow:
								activeTab === "eligibility" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						<span>2. Produits & Catégories</span>
						{selectedProductIds.length + selectedCategoryIds.length > 0 && (
							<span
								className="tn font-mono"
								style={{
									padding: "1px 5px",
									borderRadius: 4,
									background: "var(--a-surface-2)",
									color: "var(--a-ink-soft)",
									fontSize: 10,
									fontWeight: 600,
								}}
							>
								{selectedProductIds.length + selectedCategoryIds.length}
							</span>
						)}
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("targeting")}
						className="active:scale-[0.96] whitespace-nowrap"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "6px 10px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "targeting" ? 600 : 500,
							cursor: "pointer",
							border:
								activeTab === "targeting"
									? "1px solid var(--a-border)"
									: "1px solid transparent",
							transition: "all 0.15s ease",
							background:
								activeTab === "targeting" ? "var(--a-surface)" : "transparent",
							color:
								activeTab === "targeting"
									? "var(--a-ink)"
									: "var(--a-ink-mute)",
							boxShadow:
								activeTab === "targeting" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						3. Profil Client & Cumul
					</button>
				</div>

				<div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
					{errorMessage && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: 8,
								background: "rgba(220, 38, 38, 0.1)",
								border: "1px solid rgba(220, 38, 38, 0.3)",
								color: "#dc2626",
								fontSize: 12,
								fontWeight: 600,
							}}
						>
							{errorMessage}
						</div>
					)}

					{/* TAB 1 : GENERAL */}
					{activeTab === "general" && (
						<div className="grid gap-3.5">
							<div>
								<label htmlFor="auto-name-input" style={labelStyle}>
									Nom de la remise automatique{" "}
									<span style={{ color: "#d97706" }}>*</span>
								</label>
								<input
									id="auto-name-input"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="Ex. Vente Flash Électroménager -10%"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									required
								/>
							</div>

							<div>
								<label htmlFor="auto-desc-input" style={labelStyle}>
									Description ou libellé explicatif
								</label>
								<textarea
									id="auto-desc-input"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={2}
									placeholder="Ex. 10% de réduction automatique appliquée au rayon électroménager"
									className="aform-i w-full resize-y min-h-[64px]"
									style={{ color: "#0f172a", fontWeight: 500 }}
								/>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div>
									<label htmlFor="auto-type-select" style={labelStyle}>
										Type de remise <span style={{ color: "#d97706" }}>*</span>
									</label>
									<select
										id="auto-type-select"
										value={type}
										onChange={(e) =>
											setType(e.target.value as AutomaticDiscountType)
										}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									>
										<option value="PERCENT">Pourcentage (%)</option>
										<option value="FIXED">Montant fixe (FCFA)</option>
									</select>
								</div>

								<div>
									<label htmlFor="auto-value-input" style={labelStyle}>
										Valeur de la remise{" "}
										<span style={{ color: "#d97706" }}>*</span>
									</label>
									<input
										id="auto-value-input"
										type="number"
										min="0"
										max={type === "PERCENT" ? 100 : undefined}
										value={value}
										onChange={(e) =>
											setValue(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 700 }}
										required
									/>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div>
									<label htmlFor="auto-priority-input" style={inlineLabelStyle}>
										Priorité d'application
										<InfoTooltip text="En cas de plusieurs remises automatiques, les plus fortes priorités s'évaluent en premier." />
									</label>
									<input
										id="auto-priority-input"
										type="number"
										min="1"
										max="1000"
										value={priority}
										onChange={(e) =>
											setPriority(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
										placeholder="10"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									/>
								</div>

								<div>
									<span style={labelStyle}>Statut d'activation</span>
									<label className="flex items-center gap-2.5 mt-2 cursor-pointer">
										<input
											type="checkbox"
											checked={isActive}
											onChange={(e) => setIsActive(e.target.checked)}
											style={{ accentColor: "var(--a-terracotta)" }}
											className="w-4 h-4 rounded cursor-pointer"
										/>
										<span
											style={{
												fontSize: 13,
												fontWeight: 500,
												color: "#0f172a",
											}}
										>
											Activer cette remise automatique
										</span>
									</label>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
								<div>
									<label htmlFor="auto-start-input" style={labelStyle}>
										Date de début (optionnelle)
									</label>
									<input
										id="auto-start-input"
										type="datetime-local"
										value={startsAt}
										onChange={(e) => setStartsAt(e.target.value)}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 500 }}
									/>
								</div>
								<div>
									<label htmlFor="auto-end-input" style={labelStyle}>
										Date de fin (optionnelle)
									</label>
									<input
										id="auto-end-input"
										type="datetime-local"
										value={expiresAt}
										onChange={(e) => setExpiresAt(e.target.value)}
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 500 }}
									/>
								</div>
							</div>

							{name.trim() && (
								<div
									style={{
										background: "var(--a-terra-soft)",
										border: "1px solid rgba(198, 86, 58, 0.25)",
										borderRadius: 8,
										padding: "10px 14px",
										marginTop: 4,
									}}
									className="flex items-center justify-between animate-in fade-in duration-150"
								>
									<div className="flex items-center gap-2">
										<Tag variant="neutral" noDot style={{ fontWeight: 700 }}>
											P{priority || 10}
										</Tag>
										<span
											style={{
												fontSize: 12,
												fontWeight: 600,
												color: "var(--a-terra-deep)",
											}}
										>
											{name}
										</span>
									</div>
									<div
										style={{
											fontSize: 12,
											fontWeight: 700,
											color: "var(--a-terracotta)",
										}}
										className="font-mono"
									>
										{type === "PERCENT"
											? `-${value || 0}%`
											: `-${(Number(value) || 0).toLocaleString("fr-FR")} F`}
									</div>
								</div>
							)}
						</div>
					)}

					{/* TAB 2 : ELIGIBILITY */}
					{activeTab === "eligibility" && (
						<div className="grid gap-4">
							<div>
								<span style={inlineLabelStyle}>
									Restreindre à des catégories spécifiques
									<InfoTooltip text="Laissez vide pour autoriser toutes les catégories d'articles." />
								</span>
								<MultiSelect
									options={categoryOptions}
									selectedValues={selectedCategoryIds}
									onChange={setSelectedCategoryIds}
									placeholder="Toutes les catégories autorisées..."
								/>
							</div>

							<div>
								<span style={inlineLabelStyle}>
									Restreindre à des produits spécifiques
									<InfoTooltip text="Laissez vide pour autoriser tous les produits." />
								</span>
								<MultiSelect
									options={productOptions}
									selectedValues={selectedProductIds}
									onChange={setSelectedProductIds}
									placeholder={
										selectedCategoryIds.length > 0
											? "Tous les produits des catégories sélectionnées..."
											: "Tous les produits autorisés..."
									}
									emptyMessage={
										selectedCategoryIds.length > 0
											? "Aucun produit trouvé dans les catégories sélectionnées"
											: "Aucun produit correspondant"
									}
								/>
							</div>

							<div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200">
								<div>
									<label htmlFor="auto-min-subtotal-input" style={labelStyle}>
										Sous-total minimum d'achat (FCFA)
									</label>
									<input
										id="auto-min-subtotal-input"
										type="number"
										min="0"
										step="500"
										value={minSubtotal}
										onChange={(e) =>
											setMinSubtotal(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
										placeholder="0 = aucun minimum"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									/>
								</div>

								<div>
									<label
										htmlFor="auto-max-discount-input"
										style={inlineLabelStyle}
									>
										Plafond max de remise (FCFA)
										<InfoTooltip text="Pour les remises en %, limite le montant maximal accordé en FCFA. 0 = illimité." />
									</label>
									<input
										id="auto-max-discount-input"
										type="number"
										min="0"
										step="500"
										value={maxDiscount}
										onChange={(e) =>
											setMaxDiscount(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
										placeholder="0 = illimité"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
									/>
								</div>
							</div>
						</div>
					)}

					{/* TAB 3 : TARGETING & CUMULATION */}
					{activeTab === "targeting" && (
						<div className="grid gap-4">
							<div>
								<label htmlFor="auto-target-select" style={inlineLabelStyle}>
									Cible Profil Client
									<InfoTooltip text="Permet d'appliquer la remise uniquement aux nouveaux clients (1ère commande), aux clients B2B ou B2C." />
								</label>
								<select
									id="auto-target-select"
									value={customerScope}
									onChange={(e) =>
										setCustomerScope(e.target.value as CustomerScope)
									}
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
								>
									<option value="ALL">Tous les clients</option>
									<option value="FIRST_ORDER">
										Première commande uniquement (Nouveaux clients)
									</option>
									<option value="B2B">Clients B2B uniquement</option>
									<option value="B2C">Clients B2C uniquement</option>
								</select>
							</div>

							<div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-3">
								<h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
									Règles de Cumul
								</h4>

								<label className="flex items-start gap-3 cursor-pointer">
									<input
										type="checkbox"
										checked={allowCumulationWithCoupons}
										onChange={(e) =>
											setAllowCumulationWithCoupons(e.target.checked)
										}
										style={{ accentColor: "var(--a-terracotta)" }}
										className="w-4 h-4 mt-0.5 rounded cursor-pointer"
									/>
									<div>
										<span className="block text-xs font-semibold text-slate-800">
											Cumulable avec les codes promo
										</span>
										<span className="block text-[11px] text-slate-500">
											Si activé, le client pourra combiner cette remise
											automatique avec un code promo saisi.
										</span>
									</div>
								</label>

								<label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-slate-200">
									<input
										type="checkbox"
										checked={allowCumulationWithOtherAuto}
										onChange={(e) =>
											setAllowCumulationWithOtherAuto(e.target.checked)
										}
										style={{ accentColor: "var(--a-terracotta)" }}
										className="w-4 h-4 mt-0.5 rounded cursor-pointer"
									/>
									<div>
										<span className="block text-xs font-semibold text-slate-800">
											Cumulable avec d'autres remises automatiques
										</span>
										<span className="block text-[11px] text-slate-500">
											Si désactivé, seule la meilleure remise automatique
											s'appliquera au panier.
										</span>
									</div>
								</label>
							</div>
						</div>
					)}
				</div>

				<ModalFooter>
					<AdminButton
						variant="ghost"
						type="button"
						onClick={onClose}
						disabled={isPending}
					>
						Annuler
					</AdminButton>
					<AdminButton variant="primary" type="submit" disabled={isPending}>
						{isEditing ? "Enregistrer" : "Créer la remise"}
					</AdminButton>
				</ModalFooter>
			</form>
		</Modal>
	);
}
